import { action, observable } from 'mobx';
// import { api } from '../config';

// Axios (used for loading static translation file)
import axios from 'axios';
import { api } from '../config';

// UserStore (used to check app install domain)
import UserStore from './UserStore';

// Helpers
import { getData, mergeDeep } from '../helpers';

let obx = observable({
  firstMount: true,

  /*-------------------------------------------
    Loading
  -------------------------------------------*/
  loadingCalls: [],
  loading: false,
  loadingClass: '',

  /**
	 * startLoading - Sets loading state to true and adds the provided request to the array of current requests
   * @param {string} request - The name of the request being loaded
	 */
  startLoading: action( function(request) {

    obx.loading = true;
    obx.loadingCalls.push(request)
  }),

  /**
	 * finishLoading - Removes the provided request from the array of requests and sets the loading state to false if the request array is empty
   * @param {string} request - The name of the request being loaded
	 */
  finishLoading: action( function(request) {
    const requestIndex = obx.loadingCalls.indexOf(request);

    if(requestIndex >= 0) {
      obx.loadingCalls.splice(requestIndex, 1);
    } else {
      obx.loadingCalls.length = 0;
    }

    if(obx.loadingCalls.length === 0 || typeof request === 'undefined') {
      obx.loadingClass = 'leave';
      obx.loading = false;

      // Wait for loader to fade out before removing
      setTimeout(function(){
        obx.loadingClass = '';
        obx.loadingMessage = null;
      }, 250);
    }
  }),

  /*-------------------------------------------
    Error handling
  -------------------------------------------*/
  /**
	 * throwError - Determines whether to show a specific or general error message
   * @param {object} error
	 */
  throwError: action(function(error){
    obx.finishLoading();


    console.error(error);

    if(typeof error.response !== 'undefined') {
      // API errors will return an error object with a response
      // which means we have a message for them
      obx.showErrorMsg(error.response.data);
    } else {
      // If there’s no response with the error, it’s a server
      // connection error (i.e. axios can’t reach the URL)
      obx.showErrorMsg();
    }
  }),

  /**
	 * showErrorMsg - Shows a pop-up dialog with the appropriate error message for a provided error
   * @param {object} data - The error data
   * @param {string} data.code - The error code
	 */
  showErrorMsg: action(function(data){
    obx.dialog = true;
    obx.dialogContent.heading = obx.translation.Errors.heading;
    obx.dialogContent.button = obx.translation.Errors.button;

    if(!data) {
      // Server error (API call returned no data)
      obx.dialogContent.body = "no data returned";
    } else {
      // API call returned data
      if(typeof obx.translation.Errors[data.code] !== 'undefined') {
        // Error translation exists
        obx.dialogContent.body = obx.translation.Errors[data.code].message;
      } else {
        // Error translation does not exist
        console.warn('Error code not present in translation file. Falling back to API response message');
        obx.dialogContent.body = data.message;
      }
    }

    // Redirect expired session
    if(data && data.code === '5002') {
      window.location = ('/session-expired');
    }
  }),

  /*-------------------------------------------
    Translation
  -------------------------------------------*/
  locale: 'en',
  translation: null,
  translationLoaded: false,

  /**
	 * getTranslation - Loads the appropriate translation file
   * @param {string} locale
	 */
  getTranslation: action((locale) =>{
    obx.translationLoaded = true;

    // Timestamp to bust cache
    const timestamp = (new Date()).getTime();

    obx.startLoading('getTranslation');

    axios.get(`/json/${obx.locale}.json?t=${timestamp}`)
      .then(response => {
        obx.translation = response.data;
        obx.finishLoading('getTranslation');

        // TEMP: hardcode Course Planning Tool or PLG translation override file
        if(UserStore.domainName === 'wgu-plg' || getData('domainName') === 'wgu-plg') {
          obx.getTranslationOverride('/json/wgu-plg.json');
        } else {
          obx.getTranslationOverride('/json/wgu.json');
        }
      })
      .catch(error => {
        obx.throwError(error);
      });
  }),

  /**
   * getTranslationOverride - Loads an installation’s languge override file
   * @param {string} src
   */
  getTranslationOverride: action((src) => {
    // Timestamp to bust cache
    const timestamp = (new Date()).getTime();

    obx.startLoading('getTranslationOverride');

    axios.get(`${src}?t=${timestamp}`)
      .then(response => {
        const override = response.data;
        obx.translation = mergeDeep(obx.translation, override);
        obx.finishLoading('getTranslationOverride');
      })
      .catch(error => {
        obx.throwError(error);
      });
  }),

  /**
   * setTranslationOverride - Override translation file from JSON
   * @param {object} translationOverride
   */
  setTranslationOverride: action((translationOverride) => {
    try {
      obx.startLoading('setTranslationOverride');
      obx.translation = mergeDeep(obx.translation, translationOverride);
      obx.finishLoading('setTranslationOverride');
    } catch(error) {
      obx.throwError(error);
    }
  }),
  

  /*-------------------------------------------
    Dialog box
  -------------------------------------------*/
  dialog: false,
  dialogType: null,
  dialogContent: {},
  dialogCallback: null,
  dialogCancelCallback: null,
  dialogCountdown: null,
  dialogOnExpire: null,

  /**
	 * showDialog - Shows a pop-up dialog box with the provided content
   * @param {object} content
   * @param {string} type
   * @param {function} callback
   * @param {function} cancelCallback
	 */
  showDialog: action(function(content, type, callback, cancelCallback){
    if(content) {
      obx.dialogContent = content;
    }

    if(type) {
      obx.dialogType = type;
    }

    if(callback) {
      obx.dialogCallback = callback;
    }

    if(cancelCallback) {
      obx.dialogCancelCallback = cancelCallback;
    }

    obx.dialog = true;

    // Focus the dialog
    const dialogTitle = document.querySelector('.dialog__title');

    if(dialogTitle) {
      dialogTitle.focus();
    }
  }),

  /**
	 * closeDialog - Closes the pop-up dialog box
   * @param {object} content
	 */
  closeDialog: action(function(){
    obx.dialog = false;
    obx.dialogType = null;
    obx.dialogContent = {};
    obx.dialogCallback = null;
    obx.dialogCancelCallback = null;
    obx.dialogCountdown = null;
    obx.dialogOnExpire = null;

    // Focus the main content element
    const main = document.querySelector('#main-content');

    if(main) {
      main.focus();
    }
  }),

  /*-------------------------------------------
    Drawer
  -------------------------------------------*/
  drawerContent: null,
  drawerMod: null,
  drawerTitle: null,
  drawerToolbar: null,
  drawerUrl: null,

  /**
	 * closeDrawer - Closes the drawer
   * @param {event} e
   * @param {element} drawer
	 */
  closeDrawer: action(function(e){
    if(e) {
      e.preventDefault();
    }

    const drawer = document.querySelector('.drawer');

    if(drawer) {
      drawer.className += ' drawer--hidden';
    }

    // Wait until animation has completed
    // to clear out drawer
    setTimeout(function(){
      obx.drawerContent = null;
      obx.drawerMod = null;
      obx.drawerTitle = null;
      obx.drawerToolbar = null;
      obx.drawerUrl = null;

      document.body.classList.remove('overlay-open');

      const main = document.querySelector('#main-content');
      const toast = document.querySelector('.toast');

      if(main && !toast) {
        main.focus();
        window.scrollTo(0, 0);
      }
    }, 250);
  }),

  /*-------------------------------------------
    Toast
  -------------------------------------------*/
  toast: null,
  toastMod: null,

  /*-------------------------------------------
    Accessibility
  -------------------------------------------*/
  highContrast: false,
  defaultFontSize: 16,
  fontSize: 16,
  dyslexic: false,
  stylesLoaded: false,

  /*-------------------------------------------
    Time
  -------------------------------------------*/
  /**
   * getCurrentTime - Gets the server time
   */
  currentTime: null,

  getCurrentTime: action(async () => {
    const url = '/currentTime';

    await api.get(url)
      .then(response => {
        obx.currentTime = response.data.currentTime;
      });

    return obx.currentTime;
  })
});

export default obx;
